import React, { useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useTheme } from './hooks/useTheme';
import Layout from './components/layout/Layout';
import ExamplePage from './pages/resources/ExamplePage';

// Pages
import HomePage from './pages/HomePage';
import ProductPage from './pages/ProductPage';
import SolutionsPage from './pages/SolutionsPage';
import PeopleAreTalkingPage from './pages/PeopleAreTalkingPage';
import ResourcesPage from './pages/ResourcesPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import LogoutPage from './pages/LogoutPage';
import ContactPage from './pages/ContactPage';
import FeaturesPage from './pages/FeaturesPage';
import IntegrationsPage from './pages/IntegrationsPage';
import CaseStudiesPage from './pages/CaseStudiesPage';
import DocumentationPage from './pages/DocumentationPage';
import GuidesPage from './pages/GuidesPage';
import WebinarsPage from './pages/WebinarsPage';
import AboutPage from './pages/AboutPage';
import ActionableHealthcareInsightsPage from './pages/ActionableHealthcareInsightsPage';
import HealthcareClaimsPage from './pages/HealthcareClaimsPage';
import PressPage from './pages/PressPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import CookiesPage from './pages/CookiesPage';
import NotFoundPage from './pages/NotFoundPage';
import DemoPage from './pages/DemoPage';
import { isReskin } from './config/siteVariant';
// Marketing re-skin pages (videxa-poc prototype content) — registered only
// when the build was compiled with REACT_APP_SITE_VARIANT=reskin.
import TpasPage from './pages/marketing/TpasPage';
import BrokersPage from './pages/marketing/BrokersPage';
import PricingPage from './pages/marketing/PricingPage';
import SecurityPage from './pages/marketing/SecurityPage';
import MarketingDemoPage from './pages/marketing/DemoPage';
import GdprAdminPage from './pages/GdprAdminPage';
import GdprAdminLoginPage from './pages/GdprAdminLoginPage';
import { GdprAdminAuthProvider } from './context/GdprAdminAuthContext';
import GdprAdminProtectedRoute from './components/GdprAdminProtectedRoute';
import TechnicalDocsProtectedRoute from './components/TechnicalDocsProtectedRoute';
import TechnicalDocsPage from './pages/TechnicalDocsPage';

// Password Reset Pages
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// CDI Trial Step Pages
import Step1Page from './pages/cdi-steps/Step1Page';
import Step1VerifyPage from './pages/cdi-steps/Step1VerifyPage';
import Step2Page from './pages/cdi-steps/Step2Page';
import Step3Page from './pages/cdi-steps/Step3Page';
import Step4Page from './pages/cdi-steps/Step4Page';
import Step5Page from './pages/cdi-steps/Step5Page';

// New Solution Pages
import AutomationPage from './pages/solutions/AutomationPage';
import AnalyticsPage from './pages/solutions/AnalyticsPage';
import IntegrationPlatformPage from './pages/solutions/IntegrationPlatformPage';
import WorkflowPage from './pages/solutions/WorkflowPage';
import DataPlatformPage from './pages/solutions/DataPlatformPage';

// Videxa Integration Solution Pages
import RevenueCycleInsightsPage from './pages/solutions/RevenueCycleInsightsPage';
import VerbalOrderAuthPage from './pages/solutions/VerbalOrderAuthPage';
import CDIPage from './pages/solutions/CDIPage';
import SpecialistsPage from './pages/solutions/SpecialistsPage';
import FinancePage from './pages/solutions/FinancePage';
import SystemAdminPage from './pages/solutions/SystemAdminPage';

// New Resource Pages
import ApiDocsPage from './pages/resources/ApiDocsPage';
import KnowledgeBasePage from './pages/resources/KnowledgeBasePage';
import CommunityPage from './pages/resources/CommunityPage';

// Feature Sub-Pages
import RevenueCyclePage from './pages/features/RevenueCyclePage';
import ClaimsDenialPage from './pages/features/ClaimsDenialPage';
import SecureIntegrationPage from './pages/features/SecureIntegrationPage';

// Solution Sub-Pages
import RevenueCycleSolutionPage from './pages/solutions/RevenueCycleSolutionPage';
import ClaimsManagementPage from './pages/solutions/ClaimsManagementPage';
import AutomationSolutionPage from './pages/solutions/AutomationSolutionPage';

// Knowledge Base Sub-Pages
import QuickStartPage from './pages/resources/knowledge-base/QuickStartPage';
import AutomationGuidePage from './pages/resources/knowledge-base/AutomationGuidePage';
import IntegrationGuidePage from './pages/resources/knowledge-base/IntegrationGuidePage';
import SecurityGuidePage from './pages/resources/knowledge-base/SecurityGuidePage';
import CommonIssuesPage from './pages/resources/knowledge-base/CommonIssuesPage';
import ErrorCodesPage from './pages/resources/knowledge-base/ErrorCodesPage';
import FAQPage from './pages/resources/knowledge-base/FAQPage';

/**
 * Main App component for the Agent Nexus
 * 
 * This component serves as the root of the application, handling:
 * - Routing configuration for all pages
 * - Theme application to the body element
 * - Scroll restoration on navigation
 * - Conditional rendering based on route type (auth vs. main layout)
 * 
 * The application uses a different layout for authentication pages (login/signup)
 * compared to the main content pages, which use the standard Layout component.
 */
function App() {
  const { theme } = useTheme();
  const location = useLocation();
  
  /**
   * Scroll to top of the page when route changes
   * Improves user experience by ensuring new pages start at the top
   */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  /**
   * Apply the current theme to the body element
   * This ensures theme colors apply to the entire page, including any elements
   * outside of the React component tree
   */
  useEffect(() => {
    document.body.className = theme;
  }, [theme]);

  // Determine if the current route is an authentication page
  // Auth pages use a different layout without the standard header/footer
  // Note: /signup now redirects to /register, so this is kept for backwards compatibility
  const isAuthPage = false;

  return (
    <GdprAdminAuthProvider>
      <div className={`app ${theme}`}>
        {!isAuthPage ? (
          // Main layout with header and footer for standard pages
          <Layout>
          <Routes>
            {/* Main navigation pages */}
            <Route path="/" element={<HomePage />} />
            <Route path="/product" element={<ProductPage />} />

            {/* Marketing re-skin pages (videxa-poc prototype content).
                These routes exist only on a re-skin build; on legacy/prod
                builds the paths fall through to the 404 route, exactly as
                they did before the re-skin. */}
            {isReskin && <Route path="/tpas" element={<TpasPage />} />}
            {isReskin && <Route path="/brokers" element={<BrokersPage />} />}
            {isReskin && <Route path="/pricing" element={<PricingPage />} />}
            {isReskin && <Route path="/security" element={<SecurityPage />} />}

            {/* Authentication pages */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login/nexuschat" element={<Navigate to="/login?redirect=nexuschat" replace />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/logout" element={<LogoutPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Solutions section pages */}
            <Route path="/solutions" element={<SolutionsPage />} />
            <Route path="/solutions/specialists" element={<SpecialistsPage />} />
            <Route path="/solutions/finance" element={<FinancePage />} />
            <Route path="/solutions/system-admin" element={<SystemAdminPage />} />
            <Route path="/solutions/revenue-cycle-insights" element={<RevenueCycleInsightsPage />} />
            <Route path="/solutions/verbal-order-authentication" element={<VerbalOrderAuthPage />} />
            <Route path="/solutions/cdi" element={<CDIPage />} />
            <Route path="/solutions/revenue-cycle" element={<RevenueCycleSolutionPage />} />
            <Route path="/solutions/claims-management" element={<ClaimsManagementPage />} />
            <Route path="/solutions/automation" element={<AutomationSolutionPage />} />
            <Route path="/automation" element={<AutomationPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/integration" element={<IntegrationPlatformPage />} />
            <Route path="/workflow" element={<WorkflowPage />} />
            <Route path="/data-platform" element={<DataPlatformPage />} />
            
            <Route path="/people-are-talking" element={<PeopleAreTalkingPage />} />
            <Route path="/cdi-trial" element={<DemoPage />} />
            
            {/* CDI Trial Step Pages - 3-Step Process */}
            <Route path="/cdi-trial/step-1" element={<Step1Page />} />
            <Route path="/cdi-trial/step-1-verify" element={<Step1VerifyPage />} />
            <Route path="/cdi-trial/step-2" element={<Step2Page />} />
            <Route path="/cdi-trial/step-3" element={<Step3Page />} />

            {/* Redirect old 5-step routes to new 3-step structure */}
            <Route path="/cdi-trial/step-4" element={<Navigate to="/cdi-trial/step-3" replace />} />
            <Route path="/cdi-trial/step-5" element={<Navigate to="/cdi-trial/step-3" replace />} />

            {/* Redirects for dead links */}
            <Route path="/signup" element={<Navigate to="/register" replace />} />
            <Route path="/demo" element={isReskin ? <MarketingDemoPage /> : <Navigate to="/cdi-trial" replace />} />
            <Route path="/plans" element={<Navigate to="/contact" replace />} />
            <Route path="/consultation" element={<Navigate to="/contact" replace />} />
            
            {/* Product section pages */}
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/features/revenue-cycle" element={<RevenueCyclePage />} />
            <Route path="/features/claims-denial" element={<ClaimsDenialPage />} />
            <Route path="/features/secure-integration" element={<SecureIntegrationPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/case-studies" element={<CaseStudiesPage />} />
            
            {/* Resources section pages */}
            <Route path="/documentation" element={<DocumentationPage />} />
            <Route path="/guides" element={<GuidesPage />} />
            <Route path="/webinars" element={<WebinarsPage />} />
            <Route path="/api-docs" element={<ApiDocsPage />} />
            <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
            <Route path="/knowledge-base/quick-start" element={<QuickStartPage />} />
            <Route path="/knowledge-base/automation" element={<AutomationGuidePage />} />
            <Route path="/knowledge-base/integration" element={<IntegrationGuidePage />} />
            <Route path="/knowledge-base/security" element={<SecurityGuidePage />} />
            <Route path="/knowledge-base/common-issues" element={<CommonIssuesPage />} />
            <Route path="/knowledge-base/error-codes" element={<ErrorCodesPage />} />
            <Route path="/knowledge-base/faq" element={<FAQPage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/resources/example" element={<ExamplePage />} />
            
            {/* Company section pages */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/actionable-healthcare-insights" element={<ActionableHealthcareInsightsPage />} />
            <Route path="/careers" element={<HealthcareClaimsPage />} />
            <Route path="/healthcare-claims" element={<HealthcareClaimsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/press" element={<PressPage />} />
            
            {/* Legal pages */}
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/cookies" element={<CookiesPage />} />

            {/* Admin pages - GDPR Admin with separate auth */}
            <Route path="/gdpradm/login" element={<GdprAdminLoginPage />} />
            <Route path="/gdpradm" element={
              <GdprAdminProtectedRoute>
                <GdprAdminPage />
              </GdprAdminProtectedRoute>
            } />

            {/* Technical Documentation - @videxa.co employees only */}
            <Route path="/technical-docs/*" element={
              <TechnicalDocsProtectedRoute>
                <TechnicalDocsPage />
              </TechnicalDocsProtectedRoute>
            } />

            {/* Fallback route for 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Layout>
      ) : (
        // Direct rendering without Layout for authentication pages
        <Routes>
          <Route path="/signup" element={<SignupPage />} />
        </Routes>
      )}
    </div>
    </GdprAdminAuthProvider>
  );
}

export default App; 
